import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import io from "socket.io-client";

// Creating a context
const SocketContext = createContext({
  handleDisconnectSocket: () => {},
});

// A component that provides the theme
export const SocketContextProvider = ({ children }) => {
  const { userData, deviceId } = useSelector((state) => state.AUTH);
  const [batchUpdated, setBatchUpdated] = useState(false);
  const [currentBatchData, setCurrentBatchData] = useState({});
  const [socket, setSocket] = useState(null);
  // Function to handle socket connection
  const handleConnectUser = (socket) => {
    socket.on("connect", () => {
      console.log("SocketId: " + socket.id + " Connected Successfully!!!");
      socket.emit("connectUser", {
        id: userData?._id,
        socketId: socket.id,
        deviceId: deviceId,
      });
    });
  };

  const getSocket = () => {
    const socketURL = process.env.REACT_APP_BASE_URL;
    const socket = io(socketURL, {
      autoConnect: true,
      reconnection: true,
      transports: ["websocket"],
    });
    return socket;
  };

  const handleDisconnectSocket = () => {
    socket?.disconnect();
    setSocket(null);
  };

  useEffect(() => {
    if (userData?._id) {
      const newSocket = getSocket();
      handleConnectUser(newSocket);
      setSocket(newSocket);

      // Detect when the user goes offline
      const handleOffline = () => {
        newSocket.disconnect();
      };

      // Detect when the user comes online
      const handleOnline = () => {
        const reconnectedSocket = getSocket();
        handleConnectUser(reconnectedSocket);
      };

      window.addEventListener("offline", handleOffline);
      window.addEventListener("online", handleOnline);

      return () => {
        window.removeEventListener("offline", handleOffline);
        window.removeEventListener("online", handleOnline);
        newSocket.disconnect();
      };
    }
  }, [userData]);

  useEffect(() => {
    if (socket) {
      socket?.on("batch_create", (data) => {
        if (data?.status === 200) {
          toast.success(data?.message);
        } else {
          toast.error(data?.message);
        }
      });
      socket?.on("status_update", (res) => {
        if (res?.status === 200) {
          const data = res?.data ?? {};
          setBatchUpdated(true);
          setCurrentBatchData(data);
          toast.success(res?.message);
        } else {
          toast.error(res?.message);
        }
      });
    }
  }, [socket]);

  return (
    <SocketContext.Provider
      value={{
        handleDisconnectSocket,
        batchUpdated,
        setBatchUpdated,
        currentBatchData,
        setCurrentBatchData,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

// A custom hook to consume the theme context
export const useSocketContext = () => useContext(SocketContext);
